import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { usePrefs } from '../prefs'
import { getChartColors } from '../theme/chartPalette'
import { TimeRange, TimeRangeValue, defaultTimeRange } from '../components/TimeRange'

// ── Types ─────────────────────────────────────────────────────────────────────

interface OverviewCards {
  total_volume: number
  total_hours: string
  avg_throughput: string
  noncompliant_count: number
  total_members: number
}

interface DonutEntry {
  category_code: string
  display_name_en: string
  display_name_cn: string
  volume: number
}

interface RegionData {
  messages: number
  hours: string
}

interface OverviewData {
  cards: OverviewCards
  donut: DonutEntry[]
  bar: Record<string, RegionData>
  range: { from: string; to: string }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildApiUrl(tr: TimeRangeValue): string {
  const params = new URLSearchParams({ range: tr.range })
  if (tr.range === 'custom') {
    if (tr.from) params.set('from', tr.from)
    if (tr.to) params.set('to', tr.to)
  }
  return `/api/overview?${params.toString()}`
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string
  value: string | number
  unit?: string
  amber?: boolean
  clickHint?: string
  onClick?: () => void
}

function StatCard({ label, value, unit, amber, clickHint, onClick }: StatCardProps) {
  const isClickable = !!onClick
  return (
    <div
      onClick={onClick}
      style={{
        flex: '1 1 160px',
        minWidth: 140,
        background: amber ? 'var(--warning-bg)' : 'var(--bg-surface)',
        border: amber ? '1.5px solid var(--warning)' : '1px solid var(--border)',
        borderRadius: 8,
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        cursor: isClickable ? 'pointer' : 'default',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s',
      }}
      title={isClickable ? clickHint : undefined}
    >
      <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: amber ? 'var(--warning)' : 'var(--text-primary)', lineHeight: 1.2 }}>
        {value}
        {unit && <span style={{ fontSize: 14, fontWeight: 400, marginLeft: 4, color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      {amber && clickHint && (
        <div style={{ fontSize: 12, color: 'var(--warning)' }}>{clickHint}</div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function OverviewPage() {
  const { t } = useLang()
  const navigate = useNavigate()
  const { accessToken, user } = useAuth()
  const { theme } = usePrefs()
  const chartColors = getChartColors(theme)

  const [timeRange, setTimeRange] = useState<TimeRangeValue>(defaultTimeRange())
  const [data, setData] = useState<OverviewData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const role = user?.role ?? ''

  useEffect(() => {
    if (!accessToken || role === 'member') return
    setLoading(true)
    setError(null)
    fetch(buildApiUrl(timeRange), { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message ?? `HTTP ${res.status}`)
        }
        return res.json() as Promise<OverviewData>
      })
      .then((d) => {
        setData(d)
        setLoading(false)
      })
      .catch((err: Error) => {
        setError(err.message ?? 'Error loading data')
        setLoading(false)
      })
  }, [timeRange, role, accessToken])

  // Member forbidden
  if (role === 'member') {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: 'var(--danger)', fontWeight: 500 }}>{t('overviewForbidden')}</p>
      </div>
    )
  }

  const cards = data?.cards
  const donutData = data?.donut ?? []
  const barRaw = data?.bar ?? {}

  // Build bar chart data: one row per region
  const barData = Object.entries(barRaw).map(([region, vals]) => ({
    region,
    messages: vals.messages,
    hours: parseFloat(vals.hours),
  }))

  const tooltipStyle = {
    background: chartColors.tooltipBg,
    color: chartColors.tooltipText,
    border: `1px solid ${chartColors.gridColor}`,
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Time range selector */}
      <div style={{ marginBottom: 20 }}>
        <TimeRange value={timeRange} onChange={setTimeRange} />
      </div>

      {loading && (
        <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>{t('loading')}</div>
      )}

      {error && (
        <div style={{ color: 'var(--danger)', padding: '8px 0' }}>{error}</div>
      )}

      {/* 5 stat cards */}
      {cards && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
          <StatCard
            label={t('overviewTotalVolume')}
            value={cards.total_volume.toLocaleString()}
          />
          <StatCard
            label={t('overviewTotalHours')}
            value={cards.total_hours}
            unit="h"
          />
          <StatCard
            label={t('overviewAvgThroughput')}
            value={cards.avg_throughput}
          />
          <StatCard
            label={t('overviewNoncompliant')}
            value={cards.noncompliant_count}
            unit={t('overviewNoncompliantUnit')}
            amber={cards.noncompliant_count > 0}
            clickHint={cards.noncompliant_count > 0 ? t('overviewNoncompliantHint') : undefined}
            onClick={cards.noncompliant_count > 0 ? () => navigate('/task-dict#noncompliant-section') : undefined}
          />
          <StatCard
            label={t('overviewTotalMembers')}
            value={cards.total_members}
          />
        </div>
      )}

      {/* Charts row */}
      {data && (
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {/* Donut chart */}
          <div style={{ flex: '1 1 340px', minWidth: 300 }}>
            <h3 style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('overviewDonutTitle')}
            </h3>
            {donutData.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('overviewNoData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="volume"
                    nameKey='display_name_en'
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {donutData.map((_, idx) => (
                      <Cell key={idx} fill={chartColors.categoryColors[idx % chartColors.categoryColors.length]} />
                    ))}
                  </Pie>
                  <ReTooltip
                    contentStyle={tooltipStyle}
                    formatter={(value, _name, props) => {
                      const p = props?.payload as DonutEntry | undefined
                      return [
                        Number(value).toLocaleString(),
                        p?.display_name_en ?? '',
                      ] as [string, string]
                    }}
                  />
                  <Legend
                    wrapperStyle={{ color: chartColors.legendColor }}
                    formatter={(_value, entry) => {
                      const p = (entry as { payload?: DonutEntry })?.payload
                      return p?.display_name_en ?? ''
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Dual Y-axis bar chart: CN/US messages vs hours */}
          <div style={{ flex: '1 1 380px', minWidth: 320 }}>
            <h3 style={{ marginBottom: 12, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>
              {t('overviewBarTitle')}
            </h3>
            {barData.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('overviewNoData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={barData}
                  margin={{ top: 8, right: 40, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridColor} />
                  <XAxis
                    dataKey="region"
                    stroke={chartColors.axisColor}
                    tick={{ fill: chartColors.axisColor }}
                  />
                  <YAxis
                    yAxisId="left"
                    orientation="left"
                    stroke={chartColors.categoryColors[0]}
                    tick={{ fill: chartColors.axisColor }}
                    label={{
                      value: t('overviewBarMessages'),
                      angle: -90,
                      position: 'insideLeft',
                      offset: 10,
                      style: { fontSize: 11, fill: chartColors.categoryColors[0] },
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={chartColors.categoryColors[1]}
                    tick={{ fill: chartColors.axisColor }}
                    label={{
                      value: t('overviewBarHours'),
                      angle: 90,
                      position: 'insideRight',
                      offset: 10,
                      style: { fontSize: 11, fill: chartColors.categoryColors[1] },
                    }}
                  />
                  <ReTooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ color: chartColors.legendColor }} />
                  <Bar
                    yAxisId="left"
                    dataKey="messages"
                    name={t('overviewBarMessages')}
                    fill={chartColors.categoryColors[0]}
                    barSize={28}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="hours"
                    name={t('overviewBarHours')}
                    fill={chartColors.categoryColors[1]}
                    barSize={28}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
